// ABOUTME: MUI theme — single source of truth for all visual customization.
// ABOUTME: Add palette, typography, spacing, and component overrides here. Never use sx props in components.
import { createTheme, styled } from '@mui/material/styles'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import Paper from '@mui/material/Paper'
import Button from '@mui/material/Button'
import MuiLink from '@mui/material/Link'
import Typography from '@mui/material/Typography'

export const theme = createTheme({
  palette: {
    primary: {
      main: '#F97316',        // Coral Orange — CTA, AppBar, brand primary
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#14B8A6',        // Teal — daubed squares, progress, success
      contrastText: '#ffffff',
    },
    info: {
      main: '#8B5CF6',        // Violet — accent, badges, special states
      contrastText: '#ffffff',
    },
    background: {
      default: '#FFF7ED',     // Warm White — page background
      paper: '#ffffff',
    },
    text: {
      primary: '#1C1917',     // Near Black — headings and body
      secondary: '#78716C',   // Slate — muted labels and captions
    },
  },
  components: {
    MuiDrawer: {
      styleOverrides: {
        paper: ({ theme }) => ({
          backgroundColor: theme.palette.background.default,
        }),
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: {
          // MUI resets padding-top to 0 when preceded by DialogTitle — override it
          // so floating TextField labels aren't clipped.
          paddingTop: '20px !important',
        },
      },
    },
  },
  typography: {
    fontFamily: '"Poppins", system-ui, sans-serif',
    fontWeightBold: 800,
    fontWeightMedium: 600,
    h1: { fontWeight: 800, fontSize: '3rem' },
    h2: { fontWeight: 800 },
    h3: { fontWeight: 800 },
    h4: { fontWeight: 800 },
    h5: { fontWeight: 600 },
    h6: { fontWeight: 600 },
  },
})

// Grid of bingo balls — 5 per row, wrapping
export const BingoBallGrid = styled(Box)({
  display: 'grid',
  gridTemplateColumns: 'repeat(5, 1fr)',
  gap: 8,
})

// Individual bingo ball — colored circle with centered white text.
// Pass called=true for a visually distinct "already called" state.
export const BingoBall = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'called',
})<{ called?: boolean }>(({ theme, called }) => ({
  aspectRatio: '1',
  borderRadius: '50%',
  background: called
    ? `radial-gradient(circle at 35% 35%, ${theme.palette.secondary.light}, ${theme.palette.secondary.dark})`
    : `radial-gradient(circle at 35% 35%, ${theme.palette.primary.light}, ${theme.palette.primary.dark})`,
  color: called ? theme.palette.secondary.contrastText : theme.palette.primary.contrastText,
  cursor: called ? 'default' : 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  textAlign: 'center',
  padding: theme.spacing(0.5),
  overflow: 'hidden',
  '& .MuiTypography-root': {
    fontWeight: 700,
    textShadow: '0 1px 3px rgba(0,0,0,0.4)',
  },
}))

export const BingoGridContainer = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(5, minmax(0, 1fr))',
  gap: 8,
  [theme.breakpoints.down('sm')]: {
    gap: 4,
  },
}))

// Outer shell — full viewport height, pushes footer to bottom
export const PageShell = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  minHeight: '100vh',
})

// Content area — grows to fill space between navbar and footer
export const PageContent = styled(Box)(({ theme }) => ({
  flex: 1,
  paddingBottom: theme.spacing(6),
}))

// Card container for page-level forms
export const FormCard = styled(Paper)(({ theme }) => ({
  maxWidth: 520,
  width: '100%',
  margin: '0 auto',
  borderRadius: theme.spacing(2),
  overflow: 'hidden',
  backgroundColor: '#FFFCF8',
}))

// Colored header bar at the top of a FormCard
export const FormCardHeader = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.primary.contrastText,
  padding: theme.spacing(3),
}))

// Body padding inside a FormCard
export const FormCardBody = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
}))

// Footer area at the bottom of a FormCard — subtle border, centered content
export const FormCardFooter = styled(Box)(({ theme }) => ({
  borderTop: `1px solid ${theme.palette.divider}`,
  padding: theme.spacing(1.5, 3),
  display: 'flex',
  justifyContent: 'center',
}))

// Right-aligned action row at the bottom of a FormCard
export const FormCardActions = styled(Box)({
  display: 'flex',
  justifyContent: 'flex-end',
})

// Card container for game views — wider than FormCard
export const GameCard = styled(Paper)(({ theme }) => ({
  maxWidth: 640,
  width: '100%',
  margin: '0 auto',
  borderRadius: theme.spacing(2),
  overflow: 'hidden',
  backgroundColor: '#FFFCF8',
}))

// Body padding inside a GameCard — tighter on mobile to maximize grid space
export const GameCardBody = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(1),
  },
}))

// Button sized to sit alongside a standard MUI outlined Select/TextField (56px tall)
export const InlineButton = styled(Button)({
  height: 56,
  whiteSpace: 'nowrap',
  flexShrink: 0,
})

// Nav bar logo link — suppresses underline and hover color change.
// Cast preserves MuiLink's polymorphic component prop type.
export const NavLogoLink = styled(MuiLink)({
  textDecoration: 'none',
  color: 'inherit',
  '&:hover': {
    color: 'inherit',
    textDecoration: 'none',
  },
}) as typeof MuiLink

// Wraps inline content so it doesn't stretch to fill a flex container
export const InlineContent = styled('span')({
  display: 'inline-flex',
})

// Left slot of a FormCardHeader row — shrinks to allow title truncation
export const FormCardHeaderStart = styled(Stack)({
  minWidth: 0,
  overflow: 'hidden',
})

// Right slot of a FormCardHeader row — never shrinks, keeps actions on one line
export const FormCardHeaderEnd = styled(Stack)({
  flexShrink: 0,
})

// URL text inside a share dialog — breaks long URLs and grows to fill available width
export const ShareLinkUrl = styled(Typography)({
  wordBreak: 'break-all',
  flexGrow: 1,
}) as typeof Typography

export const NavSpacer = styled(Box)({
  flexGrow: 1,
})

// Nav drawer list — fixed width for the mobile hamburger menu
export const NavDrawerList = styled(Box)({
  width: 220,
})

// Visible only below md breakpoint (mobile nav elements)
export const MobileOnly = styled(Box)(({ theme }) => ({
  display: 'flex',
  [theme.breakpoints.up('md')]: {
    display: 'none',
  },
}))

// Visible only at md breakpoint and above (desktop nav elements)
export const DesktopOnly = styled(Box)(({ theme }) => ({
  display: 'none',
  alignItems: 'center',
  [theme.breakpoints.up('md')]: {
    display: 'flex',
  },
}))

export const NavGap = styled(Box)(({ theme }) => ({
  width: theme.spacing(3),
}))

export const BingoGridCell = styled(Paper)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  aspectRatio: '1',
  cursor: 'pointer',
  textAlign: 'center',
  padding: theme.spacing(1),
  minWidth: 0,
  overflow: 'hidden',
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(0.5),
  },
  wordBreak: 'break-word',
  color: theme.palette.primary.main,
  fontWeight: theme.typography.fontWeightBold,
  '& .MuiTypography-root': {
    [theme.breakpoints.down('sm')]: {
      fontSize: '0.65rem',
      lineHeight: 1.2,
    },
  },
  '&.marked': {
    backgroundColor: theme.palette.secondary.main,
    color: theme.palette.secondary.contrastText,
  },
  '&.disabled': {
    cursor: 'default',
  },
}))

// Site-wide footer — sits below page content
export const AppFooter = styled(Box)(({ theme }) => ({
  borderTop: `1px solid ${theme.palette.divider}`,
  padding: theme.spacing(3, 0),
  textAlign: 'center',
}))

// Landing page section wrapper — full-width with vertical rhythm
export const LandingSection = styled(Box)(({ theme }) => ({
  padding: theme.spacing(10, 0),
}))

// Hero section — warm gradient background
export const HeroSection = styled(Box)(({ theme }) => ({
  background: `linear-gradient(160deg, ${theme.palette.background.default} 0%, #FFE4CA 100%)`,
  padding: theme.spacing(10, 0),
}))

// CTA section at page bottom — orange background
export const CtaSection = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.primary.contrastText,
  padding: theme.spacing(10, 0),
  textAlign: 'center',
}))

// Card used in the Features grid
export const FeatureCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  height: '100%',
  borderRadius: theme.spacing(2),
  border: `1px solid ${theme.palette.divider}`,
  boxShadow: 'none',
}))

// Pricing tier name — fixed two-line height (2 × h4 fontSize × lineHeight) so prices
// align vertically across cards regardless of whether the tier name wraps.
export const PricingTierName = styled(Typography)({
  minHeight: '5.25rem',
})

// Grid container for the Use Cases section — CSS grid so all tiles share equal row heights
export const UseCaseGrid = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(2, 1fr)',
  gridAutoRows: `minmax(${theme.spacing(16)}, auto)`,
  gap: theme.spacing(2),
  [theme.breakpoints.up('sm')]: {
    gridTemplateColumns: 'repeat(3, 1fr)',
  },
  [theme.breakpoints.up('md')]: {
    gridTemplateColumns: 'repeat(6, 1fr)',
  },
}))

// Tile used in the Use Cases grid
export const UseCaseTile = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  textAlign: 'center',
  borderRadius: theme.spacing(1.5),
  border: `1px solid ${theme.palette.divider}`,
  boxShadow: 'none',
  height: '100%',
}))

// Individual cell in the decorative mock bingo card in the hero
export const MockBingoCell = styled(Paper, {
  shouldForwardProp: (prop) => prop !== 'daubed',
})<{ daubed?: boolean }>(({ theme, daubed }) => ({
  aspectRatio: '1',
  borderRadius: theme.spacing(0.75),
  backgroundColor: daubed ? theme.palette.secondary.main : theme.palette.background.paper,
  boxShadow: 'none',
  border: `1px solid ${daubed ? theme.palette.secondary.main : theme.palette.divider}`,
}))

// Support text in the CTA section — slightly muted against the orange background
export const CtaSupportText = styled('p')(({ theme }) => ({
  ...theme.typography.h6,
  opacity: 0.85,
  marginTop: 0,
  marginBottom: theme.spacing(4),
}))
